import { useEffect, useState, type ReactNode } from "react";
import useAuth from "@/hooks/useAuth";
import { WidgetSpotlightProvider } from "@/contexts/WidgetSpotlightContext";
import {
  getOnboardingDevOverride,
  setOnboardingDevOverride,
  useOnboardingGate,
} from "./useOnboardingGate";
import { useOnboardingStore } from "./useOnboardingStore";
import { resetOnboardingState } from "./api";
import { trackOnboarding } from "./analytics";
import WelcomeModal from "./WelcomeModal";
import ChecklistLauncher from "./checklist/ChecklistLauncher";
import { useChecklistDetection } from "./checklist/useChecklistDetection";
import OnboardingRail from "./rail/OnboardingRail";
import { useRailDetection } from "./rail/useRailDetection";
import { useRailTarget } from "./rail/useRailTarget";
import { visibleRailSteps } from "./rail/visibleRailSteps";

/**
 * Single mount point for all onboarding UI, rendered once in the App.jsx
 * layout (main window only — popout windows portal componentMap widgets and
 * never render this tree).
 *
 * POPOUT SAFETY RULE: nothing under src/onboarding/ may be imported from
 * src/widget-components/** — onboarding UI must never reach popout windows.
 *
 * It WRAPS the content rather than sitting beside it, because it provides the
 * widget spotlight the rail points with. Same shape as DemoGuideGate, which it
 * is mutually exclusive with: that one needs a logged-out visitor, this needs
 * an authenticated trialist.
 */
export function OnboardingGate({ children }: { children?: ReactNode }) {
  const { phase, showRail, showChecklist, hasLiveAccess } = useOnboardingGate();
  const begin = useOnboardingStore((s) => s.begin);
  const beginRail = useOnboardingStore((s) => s.beginRail);
  const railExists = useOnboardingStore((s) => !!s.state?.rail);
  const { authenticated } = useAuth();
  const { targetWidgetId, targetControl } = useRailTarget();

  // Dev/testing: "reset" wipes the saved state server-side, then downgrades
  // the stored override to "force" and reloads — the flow re-runs as a
  // fresh trialist. Non-production builds only (see getOnboardingDevOverride).
  const [resetting] = useState(() => getOnboardingDevOverride() === "reset");
  useEffect(() => {
    if (!resetting || !authenticated) return;
    resetOnboardingState()
      .catch((error) => console.error("[onboarding] dev reset failed:", error))
      .finally(() => {
        setOnboardingDevOverride("force");
        window.location.reload();
      });
  }, [resetting, authenticated]);

  /**
   * Task detection runs for BOTH surfaces, not just the checklist.
   *
   * The tasks record is the durable "have they ever done this" history and
   * outlives the rail — four of its ids are now driven by rail steps, and if
   * detection only ran while the checklist was on screen those four would never
   * be written for anyone who finished the run.
   */
  useChecklistDetection(showRail || showChecklist);
  useRailDetection(showRail);

  // First ever login for an eligible trialist: persist the initial state
  // immediately so the trigger survives the tabs signal being destroyed and
  // the flow becomes resume-driven from here on.
  useEffect(() => {
    if (phase === "trigger") {
      begin();
    } else if (phase === "resume") {
      trackOnboarding("resumed");
    }
    // trackOnboarding intentionally fires once per phase settlement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  /**
   * Mint the run once the welcome flow is behind them.
   *
   * The step list is resolved HERE, once, and stored — see OnboardingRailState
   * for why it must not be recomputed per read. hasLiveAccess is the only
   * variant input: a trialist stuck in demo mode gets a coherent three-step run
   * rather than two dead steps at the front of it.
   */
  useEffect(() => {
    if (!showRail || railExists) return;
    beginRail({
      stepIds: visibleRailSteps({ hasLiveAccess }).map((s) => s.id),
    });
  }, [showRail, railExists, hasLiveAccess, beginRail]);

  if (resetting || phase === "waiting" || phase === "ineligible") {
    return <>{children}</>;
  }

  // "trigger" renders the modal too — begin() persists the initial state on
  // the same render cycle, after which the gate settles into "resume".
  const showWelcome = phase === "trigger" || phase === "resume";

  return (
    <WidgetSpotlightProvider
      targetWidgetId={targetWidgetId}
      targetControl={targetControl}
    >
      {children}
      {showWelcome && <WelcomeModal />}
      {showRail && <OnboardingRail />}
      {showChecklist && <ChecklistLauncher />}
      {import.meta.env.DEV && (
        <div
          className="hidden"
          data-onboarding-phase={phase}
          data-onboarding-rail={String(showRail)}
          data-onboarding-rail-target={targetWidgetId ?? ""}
          data-onboarding-checklist={String(showChecklist)}
        />
      )}
    </WidgetSpotlightProvider>
  );
}
